import {useMemo} from 'react';

import {ExperimentMethod} from 'studies/typed/shared/ExperimentalInputs';
import {ConditionGroup} from 'studies/typed/shared/useExperimentDesignState';

import {GetStudyDesignFromProcedureRunGraphQL as ProcedureRunNode} from './__generated/GetStudyDesignFromProcedureRunGraphQL';
import {
  getConditionReplicateEdges,
  getMethodExecutionInstanceInfoForPlacements,
} from './getStudyDesignFromProcedureRun';
import {
  buildPmeiToNodeIdMap,
  executionEdgeSpecsFromConditionGraph,
  FlowchartEdgeSpec,
} from './ProcedureRunDesignFlowchartLayoutUtils';

export interface UseProcedureRunDesignFlowchartEdgeSpecsProps {
  isFlowchartProcedureRunDesignEnabled: boolean;
  conditions: Array<ConditionGroup>;
  methods: Array<ExperimentMethod>;
  procedureRun: ProcedureRunNode;
  activeMethodId: string | null;
}

/**
 * Derives the run design flowchart's edge specs from the run's runtime condition graph. Returns no
 * edges when the flowchart UI is disabled, since only it consumes them.
 */
export function useProcedureRunDesignFlowchartEdgeSpecs({
  isFlowchartProcedureRunDesignEnabled,
  conditions,
  methods,
  procedureRun,
  activeMethodId,
}: UseProcedureRunDesignFlowchartEdgeSpecsProps): Array<FlowchartEdgeSpec> {
  return useMemo(() => {
    if (!isFlowchartProcedureRunDesignEnabled) {
      return [];
    }
    const placements = getMethodExecutionInstanceInfoForPlacements(procedureRun);
    const pmeiToNode = buildPmeiToNodeIdMap(placements, conditions);
    const edges = getConditionReplicateEdges(procedureRun);
    return executionEdgeSpecsFromConditionGraph(edges, pmeiToNode, {methods, activeMethodId});
  }, [isFlowchartProcedureRunDesignEnabled, conditions, methods, procedureRun, activeMethodId]);
}
